import {
  CalendarClock,
  CheckCircle2,
  Clock3,
  Percent,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { useBaseRates, useChangeRate, useRateHistory } from '@/hooks/use-rates';
import { handleMutationError } from '@/lib/error-handling';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { toast } from '@/stores/toast-store';

interface RateManagementTabProps {
  loanId: string;
  currentRate?: number;
  rateType?: 'FIXED' | 'VARIABLE' | 'CAPPED';
  baseRateName?: string;
  margin?: number;
  nextReviewDate?: string;
}

export function RateManagementTab({
  loanId,
  currentRate = 0,
  rateType = 'FIXED',
  baseRateName,
  margin = 0,
  nextReviewDate,
}: RateManagementTabProps) {
  const { data: history, isLoading } = useRateHistory(loanId);
  const { data: baseRates } = useBaseRates();
  const changeRate = useChangeRate();
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [newRateInput, setNewRateInput] = useState(String(currentRate));
  const [reason, setReason] = useState('');
  const [selectedBaseRate, setSelectedBaseRate] = useState(baseRateName);

  // Sanity check: if the stored rate looks like it was multiplied by 100
  // (e.g. 2400 instead of 24), detect and show the corrected value
  const displayRate = currentRate > 100 ? currentRate / 100 : currentRate;
  const rateWasAdjusted = currentRate > 100;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Spinner size="lg" className="mr-2 h-5 w-5 animate-spin" />
        Loading rate information…
      </div>
    );
  }

  const handleChangeRate = async () => {
    try {
      const parsedRate = parseFloat(newRateInput);
      if (isNaN(parsedRate) || parsedRate <= 0) {
        toast.error('Please enter a valid rate');
        return;
      }
      if (parsedRate > 100) {
        toast.error('Rate cannot exceed 100%. Enter as a percentage (e.g. 24 for 24%)');
        return;
      }
      await changeRate.mutateAsync({
        loanId,
        newRate: parsedRate,
        reason,
        margin: rateType === 'VARIABLE' ? margin : undefined,
      });
      setShowChangeForm(false);
      setNewRateInput(String(parsedRate));
      setReason('');
    } catch (error) {
      handleMutationError(error, 'Failed to change rate');
    }
  };

  const rateTypeConfig = {
    FIXED: { label: 'Fixed Rate', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    VARIABLE: { label: 'Variable Rate', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    CAPPED: { label: 'Capped Rate', color: 'bg-green-50 text-green-700 border-green-200' },
  };

  const config = rateTypeConfig[rateType as keyof typeof rateTypeConfig];
  const parsedNewRate = parseFloat(newRateInput);
  const hasValidNewRate = Number.isFinite(parsedNewRate) && parsedNewRate > 0;
  const rateDelta = hasValidNewRate ? parsedNewRate - displayRate : 0;
  const selectedBaseRateValue = Number(
    baseRates?.find((r: any) => r.name === selectedBaseRate)?.rate || 0,
  );
  const historyItems = Array.isArray(history) ? history : [];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 via-white to-white px-5 py-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-900">Current Rate</h3>
                <span
                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${config.color}`}
                >
                  {config.label}
                </span>
              </div>

              <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                <div>
                  <p className="text-4xl font-semibold tracking-tight text-gray-900">
                    {Number(displayRate).toFixed(2)}%
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Annual rate
                    <span className="ml-2 text-gray-400">
                      ({(displayRate / 12).toFixed(4)}% monthly)
                    </span>
                  </p>
                </div>

                {rateType === 'VARIABLE' && (
                  <div className="rounded-xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      Rate Composition
                    </p>
                    <p className="mt-1 text-sm font-medium text-slate-900">
                      {baseRateName || 'Base rate'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {selectedBaseRateValue.toFixed(2)}% base + {Number(margin).toFixed(2)}% margin
                    </p>
                  </div>
                )}
              </div>

              {rateWasAdjusted && (
                <div className="inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Stored rate ({Number(currentRate).toFixed(2)}%) was auto-corrected for display.
                    Save a fresh change to normalize the record.
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[420px]">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-slate-500">
                  <Percent className="h-4 w-4" />
                  <span className="text-xs font-medium">Annual</span>
                </div>
                <p className="mt-3 text-xl font-semibold text-slate-900">
                  {Number(displayRate).toFixed(2)}%
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock3 className="h-4 w-4" />
                  <span className="text-xs font-medium">Monthly</span>
                </div>
                <p className="mt-3 text-xl font-semibold text-slate-900">
                  {(displayRate / 12).toFixed(4)}%
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-slate-500">
                  <CalendarClock className="h-4 w-4" />
                  <span className="text-xs font-medium">Next Review</span>
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-900">
                  {nextReviewDate ? fmtDate(nextReviewDate) : 'Not scheduled'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">
                Manage pricing changes with a clean audit trail
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Capture the commercial reason, update the live rate, and keep the rate history
                readable for operations and QA.
              </p>
            </div>
            <Button
              variant={showChangeForm ? 'secondary' : 'primary'}
              onClick={() => setShowChangeForm(value => !value)}
              className="w-full md:w-auto md:min-w-[180px]"
            >
              {showChangeForm ? 'Close Rate Change' : 'Change Rate'}
            </Button>
          </div>
        </div>
      </section>

      {showChangeForm && (
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-2 border-b border-gray-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Change Interest Rate</h3>
              <p className="mt-1 text-sm text-gray-500">
                Submit a new rate and record the business reason for the adjustment.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {hasValidNewRate ? (
                <span className="inline-flex items-center gap-1.5">
                  {rateDelta <= 0 ? (
                    <TrendingDown className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <TrendingUp className="h-3.5 w-3.5 text-amber-600" />
                  )}
                  Delta {rateDelta >= 0 ? '+' : ''}
                  {rateDelta.toFixed(2)}%
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <Percent className="h-3.5 w-3.5" />
                  Enter a target rate
                </span>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-5">
            {rateType === 'VARIABLE' ? (
              <>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Base Rate
                    </label>
                    <select
                      value={selectedBaseRate || ''}
                      onChange={e => setSelectedBaseRate(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Select base rate…</option>
                      {baseRates?.map((rate: any) => (
                        <option key={rate.name} value={rate.name}>
                          {rate.name} ({Number(rate.rate).toFixed(2)}%)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      New Margin (%)
                    </label>
                    <input
                      type="number"
                      defaultValue={Number(margin).toFixed(2)}
                      onChange={e => {
                        const baseRate = Number(
                          baseRates?.find((r: any) => r.name === selectedBaseRate)?.rate || 0,
                        );
                        const newMargin = parseFloat(e.target.value) || 0;
                        setNewRateInput(String(baseRate + newMargin));
                      }}
                      step={0.01}
                      min={0}
                      placeholder="e.g. 2.50"
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    Projected All-in Rate
                  </p>
                  <div className="mt-2 flex flex-wrap items-end gap-3">
                    <p className="text-2xl font-semibold text-slate-900">
                      {hasValidNewRate ? parsedNewRate.toFixed(2) : '0.00'}%
                    </p>
                    <p className="text-sm text-slate-500">
                      {selectedBaseRateValue.toFixed(2)}% base +{' '}
                      {Math.max((parsedNewRate || 0) - selectedBaseRateValue, 0).toFixed(2)}% margin
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    New Rate (%)
                  </label>
                  <input
                    type="number"
                    value={newRateInput}
                    onChange={e => setNewRateInput(e.target.value)}
                    step={0.01}
                    min={0}
                    max={100}
                    placeholder="e.g. 24.00"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>
                      Monthly equivalent:{' '}
                      {hasValidNewRate ? `${(parsedNewRate / 12).toFixed(4)}%` : '—'}
                    </span>
                    <span>Current: {Number(displayRate).toFixed(2)}%</span>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    Preview
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">
                    {hasValidNewRate ? `${parsedNewRate.toFixed(2)}%` : '—'}
                  </p>
                  <p className="mt-2 text-sm text-slate-500">
                    {hasValidNewRate
                      ? `This will ${parsedNewRate >= displayRate ? 'increase' : 'decrease'} the loan rate by ${Math.abs(rateDelta).toFixed(2)}%.`
                      : 'The updated annual rate will appear here.'}
                  </p>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Reason for Change
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Explain the commercial or operational reason for the rate change…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                rows={4}
              />
              <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                <span>Visible in the rate history audit trail.</span>
                <span>{reason.trim().length} characters</span>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowChangeForm(false);
                  setNewRateInput(String(displayRate));
                  setReason('');
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleChangeRate}
                disabled={
                  changeRate.isPending ||
                  !reason.trim() ||
                  !newRateInput ||
                  parseFloat(newRateInput) <= 0
                }
                className="w-full sm:w-auto sm:min-w-[180px]"
                loading={changeRate.isPending}
              >
                Update Rate
              </Button>
            </div>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Rate History</h3>
            <p className="mt-1 text-sm text-gray-500">
              Complete record of rate amendments, approval context, and timing.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {historyItems.length} change{historyItems.length === 1 ? '' : 's'}
          </span>
        </div>

        {historyItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <CalendarClock className="h-5 w-5" />
            </div>
            <p className="mt-4 text-sm font-medium text-slate-900">No rate changes recorded</p>
            <p className="mt-1 max-w-md text-sm text-slate-500">
              Once a change is submitted, the old rate, new rate, reason, and approver will appear
              here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50/80">
                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <th className="px-5 py-3">Changed</th>
                  <th className="px-5 py-3 text-right">Old Rate</th>
                  <th className="px-5 py-3 text-right">New Rate</th>
                  <th className="px-5 py-3">Reason</th>
                  <th className="px-5 py-3">Approved By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {historyItems.map((entry: any, i: number) => {
                  const entryDate = entry.effectiveDate || entry.date;
                  const oldRate = entry.previousRate ?? entry.oldRate;
                  const approver = entry.approver || entry.approvedBy;
                  const numericOldRate = oldRate != null ? Number(oldRate) : null;
                  const numericNewRate = Number(entry.newRate);
                  const delta =
                    numericOldRate != null && Number.isFinite(numericNewRate)
                      ? numericNewRate - numericOldRate
                      : null;

                  return (
                    <tr key={i} className="align-top transition-colors hover:bg-slate-50/70">
                      <td className="px-5 py-4 text-gray-600">
                        <div className="font-medium text-slate-900">{fmtDate(entryDate)}</div>
                        <div className="mt-1 text-xs text-slate-500">{fmtDateTime(entryDate)}</div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="font-semibold text-slate-700">
                          {oldRate != null ? `${numericOldRate?.toFixed(2)}%` : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex flex-col items-end">
                          <span className="font-semibold text-slate-900">
                            {Number.isFinite(numericNewRate)
                              ? `${numericNewRate.toFixed(2)}%`
                              : '—'}
                          </span>
                          {delta != null && (
                            <span
                              className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${
                                delta < 0
                                  ? 'text-emerald-600'
                                  : delta > 0
                                    ? 'text-amber-600'
                                    : 'text-slate-500'
                              }`}
                            >
                              {delta < 0 ? (
                                <TrendingDown className="h-3.5 w-3.5" />
                              ) : delta > 0 ? (
                                <TrendingUp className="h-3.5 w-3.5" />
                              ) : null}
                              {delta === 0
                                ? 'No change'
                                : `${delta > 0 ? '+' : ''}${delta.toFixed(2)}%`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        <div className="max-w-[420px] text-sm leading-6 text-slate-700">
                          {entry.reason || '—'}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {approver ? (
                          <div>
                            <div className="font-medium text-slate-900">
                              {`${approver.firstName} ${approver.lastName}`}
                            </div>
                            {approver.email && (
                              <div className="mt-1 text-xs text-slate-500">{approver.email}</div>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
